import moment from "moment-timezone";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

const getStartDate = (user, plan) => {
  switch (plan.tierBy) {
    case "hire-date": {
      if (!user.hiringDate) {
        return null;
      }
      return moment(user.hiringDate).format("YYYY-MM-DD");
    }
    case "seniority": {
      if (!user.dateOfSeniority) {
        return null;
      }
      return moment(user.dateOfSeniority).format("YYYY-MM-DD");
    }
    default: {
      return null;
    }
  }
}

export const usePayCodeValidations = ({ user, timeOffPlans } = {}) => {
  const { t } = useTranslation();

  const getTimeOffPlan = useCallback((timeSheet, timeOffPlans) => {
    return timeOffPlans?.find((plan) => {
      return (
        plan.startDate <= timeSheet.date
        && plan.endDate >= timeSheet.date
        && plan.plan.payCode === timeSheet.payCode?.id
        // the probation pay code should also be included here
      );
    });
  }, []);

  const isRequired = useCallback((value, timeSheet, allTimeSheets, originalAllTimeSheets) => {
    if (!value) {
      return t("pay-code-required");
    }
    return null;
  }, [t, timeOffPlans]);

  const isTimeOffPlanRequired = useCallback((value, timeSheet) => {
    if (!value?.requireTimeOffPlan) {
      return null;
    }
    const userTimeOffPlan = getTimeOffPlan(timeSheet, timeOffPlans);
    if (!userTimeOffPlan) {
      return t('associated-time-off-plan-required');
    }
    return null;
  }, [t, timeOffPlans]);

  const isOnTimeOffProbation = useCallback((value, timeSheet, allTimeSheets, originalAllTimeSheets) => {
    const userTimeOffPlan = getTimeOffPlan(timeSheet, timeOffPlans);
    if (!userTimeOffPlan) {
      return null;
    }
    const plan = userTimeOffPlan.plan;

    // probation check
    if (!plan.prohibition?.value) {
      return null;
    }

    const startDate = getStartDate(user, plan);
    if (!startDate) {
      return t('pay-code-of-time-off-plan-user-on-prohibition-but-no-prohibition-date');
    }
    const probationDate = moment(startDate).add(plan.prohibition.value, 'days');
    if (probationDate.isAfter(moment(timeSheet.date), 'date')) {
      return t('user-on-probation');
    }

    return null;
  }, [t, timeOffPlans]);

  const hasSurpassedAvailableTimeOffBalance = useCallback((value, timeSheet, allTimeSheets, originalAllTimeSheets) => {
    const userTimeOffPlan = getTimeOffPlan(timeSheet, timeOffPlans);
    if (!userTimeOffPlan) {
      return null;
    }

    let diff = 0;
    allTimeSheets.forEach((item) => {
      const existing = originalAllTimeSheets?.find((ts) => ts.id === item.id);
      const itemDuration = moment.duration(item.duration, 'seconds').asHours();
      const existingDuration = existing ? moment.duration(existing.duration, 'seconds').asHours() : 0;
      diff += itemDuration - existingDuration;
    });
    if (diff > userTimeOffPlan.bucket.available) {
      return t('available-hours-exceeded');
    }

    return null;
  }, [t, timeOffPlans]);

  return useMemo(() => {
    return [
      isRequired,
      isTimeOffPlanRequired,
      isOnTimeOffProbation,
      hasSurpassedAvailableTimeOffBalance,
    ];
  }, [isRequired, isTimeOffPlanRequired, isOnTimeOffProbation, hasSurpassedAvailableTimeOffBalance]);
}
